import React, { PropsWithChildren } from 'react';
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react';
import { CheckIcon, ChevronDownIcon, EllipsisVerticalIcon } from '@heroicons/react/20/solid';
import Utilities from '@util/Utilities';
import FormButtonStyleState from '@app/component/form/button/FormButtonStyleState';
import { Link } from 'react-router-dom';
import SizeTranslator, { SizeType } from '@app/component/common/style/SizeTranslator';

type LinkItem = {
	name: string,
	disabled?: boolean
	link?: string,
	icon?: React.JSX.Element,
	onClick?: () => any,
	isSelected?: boolean
	badge?: React.JSX.Element,
	showSelectedCheck?: boolean
}

type Props = {
	items: LinkItem[],
	state?: FormButtonStyleState,
	portal?: boolean,
	className?: string
	extraButtonClassName?: string
	menuItemClassName?: string,
	hideArrow?: boolean,
	description?: string,
	disabled?: boolean,
	optionsSize?: SizeType
}

const ButtonLinkList: React.FunctionComponent<PropsWithChildren<Props>> = ({
																																						 children,
																																						 items,
																																						 state = 'white',
																																						 portal = false,
																																						 className,
																																						 extraButtonClassName,
																																						 description,
																																						 menuItemClassName,
																																						 hideArrow = false,
																																						 disabled = false,
																																						 optionsSize = 'large',
																																					 }) => {

	const buttonState = !!state && !disabled ? state : 'disabled';

	return <Menu as="div" className="relative inline-block text-left">
		<MenuButton disabled={disabled}
			className={!!children ? (!!className ? className :
					Utilities.classNames(
						'cursor-pointer, ' +
						'btn',
						Utilities.NVL(!!buttonState, buttonState, '', 'btn-'),
						disabled ? 'pointer-events-none' : '',
						extraButtonClassName ? extraButtonClassName : ''))
				: ''}>
			{children}
			{!!children && !disabled && !hideArrow && <ChevronDownIcon className="-mr-1 ml-1 h-5 w-5" aria-hidden="true" />}
			{!children && <EllipsisVerticalIcon className="h-4 w-4" aria-hidden="true" />}
		</MenuButton>

		{!disabled && <MenuItems anchor="bottom end"
														 className={`z-10 -mr-1 ${SizeTranslator.translate(optionsSize)} origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none divide-y divide-gray-100 mt-2`}>
			{!!description && <div className="px-4 py-3">
				<p className="text-xs text-gray-500">{description}</p>
			</div>}
			{!disabled && <div className="py-1">
				{items.map((item) => (
					<MenuItem key={item.name}>
						{({ focus }) => (
							<Link
								to={item.link!}
								onClick={item.onClick}
								className={Utilities.classNames(
									!item.disabled && focus ? 'bg-gray-100 text-gray-900' : '',
									menuItemClassName ?? 'block px-4 py-2 text-xs cursor-pointer',
									item.disabled ? 'pointer-events-none text-gray-400' : 'text-gray-700',
									item.isSelected ? 'bg-blue-50 text-blue-700' : '',
								)}
							>
								{!!item.icon ? <div
									className={Utilities.classNames('flex items-center gap-x-2', item.disabled ? 'opacity-60' : '')}>
									{item.icon}
									<span className="font-semibold block">{item.name}</span>
									{item.badge}
								</div> : <span className="font-semibold block">{item.name}</span>}
								{item.isSelected && item.showSelectedCheck !== false && <span className="absolute inset-y-0 right-0 flex items-center pr-4">
										<CheckIcon className="h-5 w-5 text-green-500"/>
								</span>}
							</Link>
						)}
					</MenuItem>
				))}
			</div>}
		</MenuItems>}
	</Menu>;
};

export default ButtonLinkList;
